import React from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, TrendingUp } from 'lucide-react';

interface HeaderProps {
  isScrolled: boolean;
  isMenuOpen: boolean;
  setIsMenuOpen: (open: boolean) => void;
  currentPage?: string;
  isMainVersion?: boolean;
}

const Header: React.FC<HeaderProps> = ({ isScrolled, isMenuOpen, setIsMenuOpen, currentPage = 'home' }) => {
  return (
    <>
      <header className={`fixed top-0 w-full z-50 transition-all duration-200 bg-white ${
        isScrolled ? 'shadow-sm border-b border-slate-200' : 'border-b border-slate-100'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">

            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
              <div className="w-8 h-8 bg-navy-500 rounded-lg flex items-center justify-center">
                <TrendingUp size={16} className="text-white" />
              </div>
              <div className="flex flex-col leading-tight">
                <span className="font-sans font-700 text-slate-900 text-sm font-bold tracking-tight">ISMGPT</span>
                <span className="font-sans text-[10px] text-slate-500 tracking-wide">AI Research</span>
              </div>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-1">
              {[
                { label: 'Home', to: '/', page: 'home' },
                { label: 'Research', to: '/blog', page: 'blog' },
                { label: 'Features', to: '/features', page: 'features' },
                { label: 'About', to: '/about', page: 'about' },
              ].map(({ label, to, page }) => (
                <Link key={page} to={to}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    currentPage === page
                      ? 'text-navy-500 bg-navy-50'
                      : page === 'blog'
                        ? 'text-navy-600 font-semibold hover:bg-navy-50'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  }`}>
                  {label}
                </Link>
              ))}
            </nav>

            {/* CTA */}
            <div className="hidden md:flex items-center gap-3">
              <a href="https://chatgpt.com/g/g-jkxrUFNja-indian-stock-market"
                target="_blank" rel="noopener noreferrer"
                className="text-sm font-semibold text-white bg-navy-500 hover:bg-navy-600 px-5 py-2.5 rounded-lg transition-colors">
                Analyse Free →
              </a>
            </div>

            <button className="md:hidden text-slate-600" onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={isMenuOpen}>
              {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="fixed inset-0 bg-white z-40 md:hidden pt-16">
          <nav className="flex flex-col px-6 py-6 gap-1 border-t border-slate-100">
            {[
              { label: 'Home', to: '/' },
              { label: 'Research', to: '/blog' },
              { label: 'Features', to: '/features' },
              { label: 'About', to: '/about' },
            ].map(({ label, to }) => (
              <Link key={to} to={to} onClick={() => setIsMenuOpen(false)}
                className="py-3 px-4 text-base font-medium text-slate-700 hover:text-navy-600 hover:bg-navy-50 rounded-lg border-b border-slate-100 last:border-0">
                {label}
              </Link>
            ))}
            <a href="https://chatgpt.com/g/g-jkxrUFNja-indian-stock-market"
              target="_blank" rel="noopener noreferrer"
              className="mt-4 text-sm font-semibold text-white bg-navy-500 px-5 py-3.5 rounded-lg text-center">
              Analyse Free →
            </a>
          </nav>
        </div>
      )}
    </>
  );
};

export default Header;
